import {Observable, Subject, Subscriber, Subscription, ReplaySubject} from 'rxjs';
import {filter, take} from 'rxjs/operators';
import {ty} from '@/da/tingyun';
import {AnonymousSubject} from './AnonymousSubject';

class MockWebSocket {
  close() {}
  send() {}
}

const DEFAULT_WEBSOCKET_CONFIG = {
  url: '',
  pingPongInterval: 10000,
  pingPongTimeout: 10000,
  WebSocketCtor: typeof WebSocket === 'undefined' ? MockWebSocket : WebSocket,
  deserializer: (e) => JSON.parse(e.data),
  serializer: (value) => JSON.stringify(value),
};

const WEBSOCKETSUBJECT_INVALID_ERROR_OBJECT = 'WebsocketSubject.error must be called with an object with an error code, and an optional reason: { code: number, reason: string }';

export class WebsocketSubject extends AnonymousSubject {
  /**
   *
   * @param config.url
   * @param config.urlObservable
   * @param config.protocol
   * @param config.binaryType
   * @param config.pingPongInterval
   * @param config.pingPongTimeout
   * @param config.WebSocketCtor
   * @param config.serializer
   * @param config.deserializer
   *
   * @param config.openingObserver
   * @param config.openObserver
   * @param config.closingObserver
   * @param config.closeObserver
   * @param config.delayObserver
   * @param config.pingPongTimeoutObserver
   */
  constructor(config) {
    super();

    this.idCounter = 1;
    this.url = null;
    this.isReseted = true;
    this._socket = null;
    this._config = Object.assign({}, DEFAULT_WEBSOCKET_CONFIG, config);
    this._output = new Subject();

    this.destination = new ReplaySubject();
  }

  lift(operator) {
    const sock = new WebsocketSubject(this._config);
    sock.operator = operator;
    sock.source = this;
    return sock;
  }

  _resetState() {
    this.isReseted = true;
    this._socket = null;
    if (!this.source) {
      this.destination = new ReplaySubject();
    }
    this._output = new Subject();
  }

  /**
   * 发送消息，并返回 id 相同的 数据流
   * @param message
   * @returns {Observable<never>|Observable<unknown>}
   */
  send(message) {
    const id = this.idCounter++;
    this.next({...message, id});
    return this._output.pipe(
      filter((o) => o.id === id),
      take(1)
    );
  }

  _connectSocket(url) {
    const {WebSocketCtor, openingObserver, protocol, binaryType} = this._config;
    if (openingObserver) {
      openingObserver.next();
    }
    const observer = this._output;
    let socket = null;
    const wsCreateTime = Date.now();
    try {
      socket = protocol ? new WebSocketCtor(url, protocol) : new WebSocketCtor(url);
      this._socket = socket;
      if (binaryType) {
        this._socket.binaryType = binaryType;
      }
    } catch (e) {
      ty.createEvent({key: 'ws_open_fail', duration: Date.now() - wsCreateTime});
      observer.error(e);
      return;
    }

    const subscription = new Subscription(() => {
      this._socket = null;
      if (socket && socket.readyState === 1) {
        socket.close();
      }
    });

    socket.onopen = (e) => {
      ty.createEvent({key: 'ws_open_success', duration: Date.now() - wsCreateTime});
      const {_socket} = this;
      if (!_socket) {
        socket.close();
        this._resetState();
        return;
      }

      const {openObserver} = this._config;
      if (openObserver) {
        openObserver.next(e);
      }

      const queue = this.destination;
      this.destination = Subscriber.create(
        (x) => {
          if (socket.readyState === 1) {
            try {
              const {serializer} = this._config;
              if (x.id === undefined) {
                x.id = this.idCounter++;
              }
              socket.send(serializer(x));
            } catch (e) {
              this.destination.error(e);
            }
          }
        },
        (e) => {
          const {closingObserver} = this._config;
          if (closingObserver) {
            closingObserver.next(undefined);
          }
          if (e && e.code) {
            socket.close(e.code, e.reason);
          } else {
            observer.error(new TypeError(WEBSOCKETSUBJECT_INVALID_ERROR_OBJECT));
          }
          this._resetState();
        },
        () => {
          const {closingObserver} = this._config;
          if (closingObserver) {
            closingObserver.next(undefined);
          }
          socket.close();
          this._resetState();
        }
      );
      if (queue && queue instanceof ReplaySubject) {
        subscription.add(queue.subscribe(this.destination));
      }
      this._startPingPong();
    };
    socket.onerror = (e) => {
      ty.createEvent({key: 'ws_error', duration: Date.now() - wsCreateTime});
      this._resetState();
      observer.error(e);
    };
    socket.onclose = (e) => {
      ty.createEvent({key: 'ws_close', duration: Date.now() - wsCreateTime});
      this._resetState();
      const {closeObserver} = this._config;
      if (closeObserver) {
        closeObserver.next(e);
      }
      if (e.wasClean) {
        observer.complete();
      } else {
        observer.error(e);
      }
    };
    socket.onmessage = (e) => {
      try {
        const {deserializer} = this._config;
        observer.next(deserializer(e));
      } catch (err) {
        observer.error(err);
      }
    };
  }

  _startPingPong() {
    const {_socket} = this;
    if (!_socket || _socket.readyState !== 1) {
      return;
    }

    const wsPingTime = Date.now();
    const timeout = setTimeout(() => {
      // 心跳超时
      const {pingPongTimeoutObserver} = this._config;
      if (pingPongTimeoutObserver) {
        pingPongTimeoutObserver.next();
      }
      ty.createEvent({key: 'ws_heartbeat_timeout', duration: Date.now() - wsPingTime});
    }, this._config.pingPongTimeout);

    const {_output} = this;
    _output.pipe(take(1)).subscribe(() => {
      // 只要有消息就视为心跳有响应
      clearTimeout(timeout);
    });

    const id = this.idCounter++;
    _output
      .pipe(
        filter((o) => o.id === id),
        take(1)
      )
      .subscribe(() => {
        const {delayObserver, pingPongInterval} = this._config;
        if (delayObserver) {
          delayObserver.next(Date.now() - wsPingTime);
        }
        setTimeout(() => this._startPingPong(), pingPongInterval);
      });
    this.next({method: 'server.ping', params: [], id});
  }

  _subscribe(subscriber) {
    const {source} = this;
    if (source) {
      return source.subscribe(subscriber);
    }

    if (this.isReseted) {
      this.isReseted = false;
      const {_config} = this;
      const {urlObservable} = _config;
      const url = this.url || _config.url;
      if (url) {
        this._connectSocket(url);
      } else if (urlObservable) {
        urlObservable.pipe(take(1)).subscribe((url) => {
          this.url = url;
          this._connectSocket(url);
        });
      }
    }
    this._output.subscribe(subscriber);
    subscriber.add(() => {
      const {_socket} = this;
      if (this._output.observers.length === 0) {
        if (_socket && _socket.readyState === 1) {
          _socket.close();
        }
        this._resetState();
      }
    });
    return subscriber;
  }

  unsubscribe() {
    const {_socket} = this;
    if (_socket && _socket.readyState === 1) {
      _socket.close();
    }
    this._resetState();
    super.unsubscribe();
  }
}
